import { useContext, useEffect, useState } from "react";
import * as turf from "@turf/turf";

//context
import MapContext from "../../../services/context/map-context";

// utils
import {
  addClipLayer,
  addInnerPropertyLayer,
  addPropertyAreaLabel,
  addPropertyDistanceLabels,
  addPropertyEdgesLayer,
  addPropertyLayer,
  addPropertyVertexLabels,
  addPropertyVertexPointLayer,
  removeAllPropertyLabels,
  removeClipLayer,
  removeInnerPropertyLayer,
  removePropertyEdgesLayer,
  removePropertyLayer,
  removePropertyVertexPointLayer,
} from "../../../utils/map-layer";
import { createDistanceAndVertexFeatures } from "../../../utils/map-labels";
import { formatArea } from "../../../utils/formatting";
import { polygonToLines } from "../../../utils/map-polygon";
import {
  calculateMaxBorderWidth,
  calculateOffsetPolygon,
} from "../../../utils/map-property-border";
import { useAppContext } from "../../../services/context/app-context";

export const DefineBorder = ({ propertyData, updatePropertyData }) => {
  const { map, mapLoaded } = useContext(MapContext);
   const { showBaseBuildings } = useAppContext();

  const { propertyGeoJSON, minBorderDistance, selectedLine } = propertyData;

  const [maxBorderWidth, setMaxBorderWidth] = useState(null);

  // Update max border width when property polygon is available
  useEffect(() => {
    if (propertyGeoJSON) {
      const maxWidth = calculateMaxBorderWidth(propertyGeoJSON);
      setMaxBorderWidth(maxWidth);
      updatePropertyData("maxBorder", maxWidth);
    }
  }, [propertyGeoJSON]);

  useEffect(() => {
    if (map && mapLoaded && propertyGeoJSON) {
      addPropertyLayer(map, propertyGeoJSON);
      if(!showBaseBuildings){
        addClipLayer(map, propertyGeoJSON);
      }
      addPropertyLabels(propertyGeoJSON);
      addEdgesAndEntrySide(propertyGeoJSON, selectedLine);
      const explodedFC = turf.explode(propertyGeoJSON.features[0]);
      addPropertyVertexPointLayer(map, explodedFC);

      /* if (propertyGeoJSON) {
        // Fit the map to the property
        const coordinates =
          propertyGeoJSON.features[0].geometry.coordinates[0][0];

        map.flyTo({ center: coordinates, zoom: 17 });
      } */
    }

    // Cleanup on unmount
    return () => {
      if (map && map._container?.parentNode) {
        removePropertyLayer(map);
        removeClipLayer(map);
        removeAllPropertyLabels(map);
        removePropertyEdgesLayer(map);
        removeInnerPropertyLayer(map);
        removePropertyVertexPointLayer(map);
      }
    };
  }, [map, mapLoaded]);

  
      // hide base buildings
  useEffect(() => {
    if (!showBaseBuildings && propertyGeoJSON) {
      addClipLayer(map, propertyGeoJSON);
    }
    if (showBaseBuildings) {
      removeClipLayer(map);
    }
  }, [showBaseBuildings]);

  // On each render or when minBorderDistance changes
  useEffect(() => {
    if (map && mapLoaded && propertyGeoJSON) {
      // Show the zone-area (negative buffer)
      const offsetPolygon = calculateOffsetPolygon(
        propertyGeoJSON,
        minBorderDistance
      );
      if (offsetPolygon) {
        const area = turf.area(offsetPolygon);
        const centroid = turf.center(offsetPolygon);

        addInnerPropertyLayer(map, offsetPolygon);
        addPropertyAreaLabel(map, {
          type: "FeatureCollection",
          features: [
            {
              type: "Feature",
              geometry: centroid.geometry,
              properties: {
                area: `${formatArea(area).value} ${formatArea(area).unit}`,
              },
            },
          ],
        });
        updatePropertyData("area", Math.round(area * 100) / 100);
      }
    }
  }, [map, propertyGeoJSON, minBorderDistance]);

  const calculateZoneBetweenPolygons = (originalPolygon, offsetPolygon) => {
    let zone = null;
    try {
      zone = turf.difference(
        turf.featureCollection([originalPolygon, offsetPolygon])
      );
    } catch (error) {
      console.error("Error calculating zone between polygons:", error);
      zone = null;
    }
    return zone;
  };

  // Figure out the largest possible border without going negative
  /*  const calculateMaxBorderWidth = (polygonGeoJSON) => {
    // calculate the minimum distance from the centroid to the polygon edges
    const centroid = turf.center(polygonGeoJSON);
    const lineToPolygon = turf.lineString(
      polygonGeoJSON.features[0].geometry.coordinates[0]
    );

    
    const distances = turf
      .explode(lineToPolygon)
      .features.map((point) =>
        turf.distance(centroid, point, { units: "meters" })
      );

   
    const minDistance = Math.min(...distances);
    const maxWidth = minDistance;
    return maxWidth;
  }; */

  const addPropertyLabels = (propertyGeoData) => {
    const propertyFeature = propertyGeoData.features[0];
    const { distanceFeatures, vertexFeatures } =
      createDistanceAndVertexFeatures(propertyFeature);

    addPropertyDistanceLabels(map, {
      type: "FeatureCollection",
      features: distanceFeatures,
    });

    addPropertyVertexLabels(map, {
      type: "FeatureCollection",
      features: vertexFeatures,
    });
  };

  const addEdgesAndEntrySide = (propertyFeature, selectedLine) => {
    if (!propertyFeature || !selectedLine) return;
    const lineFeatures = polygonToLines(propertyFeature.features[0]);
    const edgesGeoData = {
      type: "FeatureCollection",
      features: lineFeatures,
    };

    addPropertyEdgesLayer(map, edgesGeoData, selectedLine);
  };

  return null;
};
